/**
 * ShareButton.jsx — Phase 9 Shareability trigger
 *
 * Button component that:
 *   - Free tier: renders a locked chip with tooltip "Available on Professional+".
 *   - Professional+: renders a cyan CTA. On click, renders a hidden <ShareCard>,
 *     rasterises it with html2canvas → PNG Blob → opens native share sheet
 *     (navigator.share) if available, else shows a modal with download/copy.
 *   - Tracks the export via POST /api/share/track for Speculo analytics.
 *
 * API
 * ---
 *   <ShareButton variant="primary" showLabel streak={…} streakBest={…} ... />
 *
 *   variant: "primary" | "secondary"
 *   showLabel: whether to display the text label or just the icon
 */
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Share2, Loader2, Download, Copy, X, Lock } from "lucide-react";
import html2canvas from "html2canvas";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { getLocalDateString } from "@/lib/date";
import ShareCard from "@/components/share/ShareCard";

const SHARE_ALLOWED_TIERS = ["professional", "executive", "elite"];

async function blobFromCanvas(canvas) {
  return new Promise((resolve) => canvas.toBlob((b) => resolve(b), "image/png", 0.95));
}

function tierAllowsShare(user) {
  const tier = user?.subscription?.tier || "free";
  return SHARE_ALLOWED_TIERS.includes(tier);
}

async function fetchShareData() {
  const today = getLocalDateString();
  const [logrosR, metricasR] = await Promise.all([
    api.get(`/logros?date=${encodeURIComponent(today)}`),
    api.get("/metricas"),
  ]);
  const logros = (logrosR.data && logrosR.data.logros) || [];
  const m = metricasR.data || {};
  return {
    logros,
    streak: Number(m.streak || 0),
    streakBest: Number(m.streak_best || 0),
    totalPerfect: Number((m.estrella && m.estrella.total) || 0),
    date: new Date(),
  };
}

export default function ShareButton({
  variant = "primary",
  showLabel = true,
  logros: logrosProp,
  streak: streakProp,
  streakBest: streakBestProp,
  totalPerfect: totalPerfectProp,
  date: dateProp,
  testId = "share-button",
  className = "",
}) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const cardRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | generating | preview | error
  const [pngUrl, setPngUrl] = useState(null);
  const [pngBlob, setPngBlob] = useState(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // Data snapshot used by the mounted ShareCard. Populated on click.
  const [cardData, setCardData] = useState({
    logros: logrosProp || [],
    streak: streakProp || 0,
    streakBest: streakBestProp || 0,
    totalPerfect: totalPerfectProp || 0,
    date: dateProp || new Date(),
  });

  const allowed = tierAllowsShare(user);

  const track = useCallback(async (platform) => {
    try {
      await api.post("/share/track", { event: "share_card_generated", platform });
    } catch {
      // Silent — analytics must not block UX.
    }
  }, []);

  const generate = useCallback(async () => {
    if (!allowed || state === "generating") return;
    setState("generating");
    setPngUrl(null);
    setPngBlob(null);

    // If caller didn't pass live numbers, fetch them fresh. This makes
    // <ShareButton /> a drop-in component with no required props.
    let snapshot = {
      logros: logrosProp || [],
      streak: streakProp || 0,
      streakBest: streakBestProp || 0,
      totalPerfect: totalPerfectProp || 0,
      date: dateProp || new Date(),
    };
    const needsFetch =
      !logrosProp || logrosProp.length === 0 ||
      streakProp == null || streakBestProp == null || totalPerfectProp == null;
    if (needsFetch) {
      try {
        const fresh = await fetchShareData();
        snapshot = { ...snapshot, ...fresh };
      } catch {
        // Fallback: render whatever we have.
      }
    }
    setCardData(snapshot);
    // Wait a tick so React mounts the hidden ShareCard with new data.
    await new Promise((r) => setTimeout(r, 60));
    try {
      const node = cardRef.current;
      if (!node) throw new Error("card_not_mounted");
      const canvas = await html2canvas(node, {
        backgroundColor: "#0A0E1A",
        scale: 1,
        useCORS: true,
        logging: false,
        width: 1080,
        height: 1080,
      });
      const blob = await blobFromCanvas(canvas);
      if (!blob) throw new Error("png_conversion_failed");
      const url = URL.createObjectURL(blob);
      setPngBlob(blob);
      setPngUrl(url);

      // Native share if available (mobile + Chrome desktop).
      const file = new File([blob], "nexagenda-dia-perfecto.png", { type: "image/png" });
      if (
        typeof navigator !== "undefined" &&
        navigator.canShare &&
        navigator.canShare({ files: [file] }) &&
        navigator.share
      ) {
        try {
          await navigator.share({
            files: [file],
            title: t("share.card.hero_title"),
            text: t("share.card.hero_title"),
          });
          track("native");
          setState("idle");
          URL.revokeObjectURL(url);
          setPngUrl(null);
          setPngBlob(null);
          return;
        } catch (e) {
          // User canceled or the share failed — fall through to preview.
        }
      }
      setState("preview");
    } catch (e) {
      setState("error");
      // Auto-clear error state after 2.5s.
      setTimeout(() => setState("idle"), 2500);
    }
  }, [allowed, state, t, track, logrosProp, streakProp, streakBestProp, totalPerfectProp, dateProp]);

  const handleDownload = useCallback(() => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = "nexagenda-dia-perfecto.png";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    track("download");
  }, [pngUrl, track]);

  const handleCopy = useCallback(async () => {
    if (!pngBlob) return;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        // eslint-disable-next-line no-undef
        await navigator.clipboard.write([
          // eslint-disable-next-line no-undef
          new ClipboardItem({ "image/png": pngBlob }),
        ]);
        track("clipboard");
      }
    } catch {
      // Clipboard write can fail (permissions, unsupported). No-op.
    }
  }, [pngBlob, track]);

  const closePreview = useCallback(() => {
    if (pngUrl) URL.revokeObjectURL(pngUrl);
    setPngUrl(null);
    setPngBlob(null);
    setState("idle");
  }, [pngUrl]);

  // --- Free tier: locked chip + tooltip ---
  if (!allowed) {
    return (
      <div
        className={`relative inline-flex ${className}`}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        onFocus={() => setTooltipOpen(true)}
        onBlur={() => setTooltipOpen(false)}
      >
        <button
          type="button"
          data-testid={`${testId}-locked`}
          disabled
          className="
            inline-flex items-center gap-2
            h-10 px-4 rounded-full
            border border-white/10 bg-white/[0.03]
            text-[12.5px] text-[#6B7280]
            cursor-not-allowed
          "
        >
          <Lock className="h-4 w-4" strokeWidth={1.75} />
          {showLabel ? t("share.button_short") : null}
        </button>
        {tooltipOpen ? (
          <div
            data-testid={`${testId}-tooltip`}
            className="
              absolute bottom-full mb-2 left-1/2 -translate-x-1/2
              whitespace-nowrap
              px-3 py-1.5 rounded-lg
              bg-[#0F1525] border border-[rgba(0,229,255,0.25)]
              text-[11px] text-[#E5E7EB]
              shadow-[0_8px_24px_rgba(0,0,0,0.5)]
              z-50
            "
          >
            {t("share.locked_tooltip")}
          </div>
        ) : null}
      </div>
    );
  }

  // --- Pro+ user flow ---
  const baseBtn =
    variant === "primary"
      ? "bg-gradient-to-r from-[rgba(0,229,255,0.18)] to-[rgba(0,229,255,0.08)] border-[rgba(0,229,255,0.5)] text-[#00E5FF] hover:from-[rgba(0,229,255,0.28)] hover:to-[rgba(0,229,255,0.14)]"
      : "bg-white/[0.03] border-[rgba(0,229,255,0.25)] text-[#00E5FF] hover:bg-[rgba(0,229,255,0.08)]";

  return (
    <>
      <button
        type="button"
        data-testid={testId}
        onClick={generate}
        disabled={state === "generating"}
        className={`
          inline-flex items-center gap-2
          h-10 px-4 rounded-full
          border ${baseBtn}
          text-[12.5px] font-medium tracking-[0.02em]
          transition-colors
          disabled:opacity-60
          ${className}
        `}
      >
        {state === "generating" ? (
          <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
        ) : (
          <Share2 className="h-4 w-4" strokeWidth={2} />
        )}
        {showLabel ? (
          <span>
            {state === "generating"
              ? t("share.preparing")
              : variant === "primary"
              ? t("share.button")
              : t("share.button_short")}
          </span>
        ) : null}
      </button>

      {/* Hidden ShareCard that html2canvas rasterises. Mounted only while generating/preview. */}
      {(state === "generating" || state === "preview") ? (
        <ShareCard
          ref={cardRef}
          logros={cardData.logros}
          streak={cardData.streak}
          streakBest={cardData.streakBest}
          totalPerfect={cardData.totalPerfect}
          date={cardData.date}
        />
      ) : null}

      {/* Preview modal (fallback when navigator.share unavailable) */}
      {state === "preview" && pngUrl ? (
        <div
          data-testid="share-preview-overlay"
          className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in-up p-4"
          role="dialog"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closePreview();
          }}
        >
          <div
            className="
              relative w-full max-w-md
              rounded-[24px] bg-[#0F1525]
              border border-[rgba(0,229,255,0.2)]
              p-5 text-[#E5E7EB]
              shadow-[0_40px_100px_rgba(0,0,0,0.7)]
            "
            data-testid="share-preview-modal"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-[16px] font-semibold">{t("share.ready")}</h3>
              <button
                type="button"
                onClick={closePreview}
                data-testid="share-preview-close"
                className="h-8 w-8 rounded-full border border-white/10 flex items-center justify-center hover:bg-white/[0.04]"
              >
                <X className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[rgba(0,229,255,0.15)] bg-black/40">
              <img
                src={pngUrl}
                alt="Perfect Day share"
                data-testid="share-preview-image"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleDownload}
                data-testid="share-preview-download"
                className="
                  h-11 rounded-full
                  bg-gradient-to-r from-[rgba(0,229,255,0.2)] to-[rgba(0,229,255,0.08)]
                  border border-[rgba(0,229,255,0.5)]
                  text-[#00E5FF] text-[13px] font-medium
                  inline-flex items-center justify-center gap-2
                  hover:from-[rgba(0,229,255,0.3)]
                "
              >
                <Download className="h-4 w-4" strokeWidth={2} />
                {t("share.download")}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                data-testid="share-preview-copy"
                className="
                  h-11 rounded-full
                  bg-white/[0.03] border border-white/10
                  text-[#E5E7EB] text-[13px] font-medium
                  inline-flex items-center justify-center gap-2
                  hover:bg-white/[0.06]
                "
              >
                <Copy className="h-4 w-4" strokeWidth={2} />
                {t("share.copy_image")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
